import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Droplets, Wrench, Home, Building2, AlertTriangle, Search, Waves } from 'lucide-react';
import { MOCK_SERVICES } from '../../mock/services.mock';
import { ROUTES } from '../../config/app.config';

const FALLBACK = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format&fit=crop&q=80';

const serviceIcons: Record<string, React.ReactNode> = {
  Droplets:      <Droplets size={28} color="white" />,
  Wrench:        <Wrench size={28} color="white" />,
  Home:          <Home size={28} color="white" />,
  Building2:     <Building2 size={28} color="white" />,
  AlertTriangle: <AlertTriangle size={28} color="white" />,
  Search:        <Search size={28} color="white" />,
  Waves:         <Waves size={28} color="white" />,
};

const serviceColors: Record<string, string> = {
  'svc-001': 'linear-gradient(135deg,rgba(16,185,129,0.8),rgba(5,150,105,0.6))',
  'svc-002': 'linear-gradient(135deg,rgba(239,68,68,0.8),rgba(185,28,28,0.6))',
  'svc-003': 'linear-gradient(135deg,rgba(249,115,22,0.8),rgba(194,65,12,0.6))',
  'svc-004': 'linear-gradient(135deg,rgba(99,102,241,0.8),rgba(67,56,202,0.6))',
  'svc-005': 'linear-gradient(135deg,rgba(239,68,68,0.9),rgba(124,45,18,0.7))',
  'svc-006': 'linear-gradient(135deg,rgba(59,130,246,0.8),rgba(29,78,216,0.6))',
  'svc-007': 'linear-gradient(135deg,rgba(20,184,166,0.8),rgba(15,118,110,0.6))',
};

const ServiceCard: React.FC<{ service: typeof MOCK_SERVICES[0]; index: number }> = ({ service, index }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const isEven = index % 2 === 0;

  return (
    <div
      className="card"
      style={{
        padding: 0, overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: isEven ? '420px 1fr' : '1fr 420px',
        minHeight: '300px',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 20px 48px -8px rgba(0,0,0,0.18)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ''; (e.currentTarget as HTMLElement).style.boxShadow = ''; }}
    >
      {/* Image Panel */}
      <div style={{ order: isEven ? 0 : 1, position: 'relative', overflow: 'hidden', minHeight: '280px' }}>
        {/* Skeleton */}
        {!imgLoaded && (
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,#1e293b 25%,#334155 50%,#1e293b 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
        )}
        <img
          src={service.image}
          alt={service.title}
          loading="lazy"
          onLoad={() => setImgLoaded(true)}
          onError={e => { (e.currentTarget as HTMLImageElement).src = FALLBACK; setImgLoaded(true); }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease', position: 'absolute', inset: 0 }}
        />
        {/* Gradient color overlay */}
        <div style={{ position: 'absolute', inset: 0, background: serviceColors[service.id] || serviceColors['svc-001'] }} />
        {/* Top-left: Icon bubble */}
        <div style={{ position: 'absolute', top: '1.25rem', left: '1.25rem', width: '3.25rem', height: '3.25rem', borderRadius: '1rem', background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {serviceIcons[service.icon]}
        </div>
        {/* Bottom: price badge */}
        <div style={{ position: 'absolute', bottom: '1.25rem', left: '1.25rem', background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', borderRadius: '0.625rem', padding: '0.4rem 0.875rem', border: '1px solid rgba(255,255,255,0.15)' }}>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Starting from</div>
          <div style={{ color: 'white', fontSize: '1.1rem', fontWeight: 800 }}>{service.price}</div>
        </div>
        {/* Popular badge */}
        {service.popular && (
          <div style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', background: '#f97316', color: 'white', padding: '0.2rem 0.75rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Popular
          </div>
        )}
      </div>

      {/* Content Panel */}
      <div style={{ order: isEven ? 1 : 0, padding: '2rem 2.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.875rem', flexWrap: 'wrap' }}>
          <span className="badge badge-info" style={{ fontSize: '0.72rem' }}>{service.duration}</span>
          <span className="badge badge-success" style={{ fontSize: '0.72rem' }}>{service.warranty}</span>
        </div>

        <h2 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: '0.625rem', color: 'var(--text-primary)', lineHeight: 1.2 }}>
          {service.title}
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.75, marginBottom: '1.25rem' }}>
          {service.shortDesc}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', marginBottom: '1.75rem' }}>
          {service.features.slice(0, 6).map(f => (
            <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              <CheckCircle size={13} color="#10b981" strokeWidth={2.5} />
              <span>{f}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <Link to={`/services/${service.slug}`} className="btn-primary" style={{ fontSize: '0.875rem' }}>
            Get a Quote <ArrowRight size={15} />
          </Link>
          <Link to={`/services/${service.slug}`} className="btn-secondary" style={{ fontSize: '0.875rem', padding: '0.625rem 1.25rem' }}>
            Learn More
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @media(max-width:900px){
          div[style*="grid-template-columns: 420px"]{grid-template-columns:1fr!important;grid-template-rows:260px auto;}
          div[style*="grid-template-columns: 1fr 420px"]{grid-template-columns:1fr!important;grid-template-rows:260px auto;}
          div[style*="order: 1"]{order:0!important;}
        }
      `}</style>
    </div>
  );
};

const ServicesPage: React.FC = () => (
  <div>
    <div className="page-header" style={{ paddingTop: '7rem' }}>
      <span style={{ color: '#f97316', fontWeight: 600, fontSize: '0.875rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Complete Service Menu</span>
      <h1 style={{ color: 'white', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, marginTop: '0.5rem', marginBottom: '1rem' }}>Professional Roofing Services</h1>
      <p style={{ color: '#94a3b8', maxWidth: '540px', margin: '0 auto', fontSize: '1rem', lineHeight: 1.7 }}>
        From routine cleaning to full commercial replacements — the same expertise and care on every project.
      </p>
    </div>

    <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
      <div className="container-xl">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {MOCK_SERVICES.map((service, i) => (
            <ServiceCard key={service.id} service={service} index={i} />
          ))}
        </div>

        {/* CTA strip */}
        <div style={{ marginTop: '4rem', background: 'linear-gradient(135deg,#0f172a,#1e293b)', borderRadius: '1.25rem', padding: '2.5rem', textAlign: 'center', border: '1px solid #334155' }}>
          <h3 style={{ color: 'white', fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem' }}>Not Sure Which Service You Need?</h3>
          <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>Our licensed estimators will assess your roof for free — no obligation.</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to={ROUTES.QUOTE} className="btn-primary">Schedule Free Inspection <ArrowRight size={16} /></Link>
            <Link to={ROUTES.CONTACT} className="btn-secondary" style={{ borderColor: '#334155', color: 'white' }}>Ask a Question</Link>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default ServicesPage;
